<template>
    <MPModal
        v-model="open"
        variant="side"
        size="md"
        title="Activity"
    >
        <template #sub-header>
            <div class="flex flex-col gap-3">
                <MyOrderActionsLiveActivity @close="open = false" />
                <!--
                    A paused queue has nowhere else to be found — the footer is its
                    only other surface, and pausing is what takes that away. It sits
                    above the day's actions because it is the thing an agent is part
                    way through, not one more thing on the list.
                -->
                <SalesQueueActivityCard @close="open = false" />
                <div class="flex items-center justify-between">
                    <button
                        type="button"
                        class="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
                        aria-label="Previous day"
                        @click="prevDay"
                    >
                        <SvgIcon
                            name="system-chevron-left"
                            class="size-5 text-muval-gray-1"
                        />
                    </button>
                    <div class="flex items-center gap-2">
                        <span class="text-sm font-medium">{{ formattedDate }}</span>
                        <PopoverRoot>
                            <PopoverTrigger as-child>
                                <button
                                    type="button"
                                    class="flex h-7 w-7 items-center justify-center rounded-full text-muval-gray-1 transition-colors hover:bg-muval-gray-6"
                                    aria-label="Settings"
                                >
                                    <SvgIcon
                                        name="system-settings"
                                        class="size-5"
                                    />
                                </button>
                            </PopoverTrigger>
                            <PopoverPortal>
                                <PopoverContent
                                    class="z-[100] rounded-lg border bg-white p-3 shadow-muval-3"
                                    :side-offset="6"
                                    align="center"
                                >
                                    <label class="flex items-center justify-between gap-x-3 text-sm">
                                        <span>Show completed</span>
                                        <MuvalSwitch
                                            v-model="showCompleted"
                                            vid="my-order-actions-show-completed"
                                        />
                                    </label>
                                </PopoverContent>
                            </PopoverPortal>
                        </PopoverRoot>
                    </div>
                    <button
                        type="button"
                        class="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
                        aria-label="Next day"
                        @click="nextDay"
                    >
                        <SvgIcon
                            name="system-chevron-right"
                            class="size-5 text-muval-gray-1"
                        />
                    </button>
                </div>
            </div>
        </template>

        <div
            v-if="isLoading"
            class="text-sm text-muval-gray-2"
        >
            Loading…
        </div>
        <Transition
            v-else
            :name="dateSlideName"
            mode="out-in"
        >
            <TransitionGroup
                :key="selectedDate"
                name="action-list"
                tag="div"
                class="relative flex flex-col gap-y-2"
            >
                <div
                    v-if="visibleActions.length === 0"
                    key="empty"
                    class="flex flex-col items-center gap-1 py-8 text-center"
                >
                    <div class="text-sm font-medium">{{ emptyTitle }}</div>
                    <div class="text-xs text-muval-gray-3">{{ emptySubtitle }}</div>
                </div>
                <MyOrderAction
                    v-for="action in visibleActions"
                    :key="action.id"
                    :action="action"
                    @click="open = false"
                />
            </TransitionGroup>
        </Transition>
        <!-- <pre
            v-else
            class="whitespace-pre-wrap break-words text-xs"
        >{{ JSON.stringify(data, null, 2) }}</pre> -->
    </MPModal>
</template>

<script setup lang="ts">
import { addDays, format, parseISO, subDays } from 'date-fns';
import { useLocalStorage } from '@vueuse/core';
import { PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from 'reka-ui';
import SalesQueueActivityCard from '@components/sales/SalesQueueActivityCard.vue';
import { useMyOrderActions } from '@/composables/useMyOrderActions';

const open = defineModel<boolean>({ required: true });

const selectedDate = ref(format(new Date(), 'yyyy-MM-dd'));
const showCompleted = useLocalStorage('my-order-actions:show-completed', false);
const dateSlideName = ref<'slide-forward' | 'slide-back'>('slide-forward');

const { data, isLoading } = useMyOrderActions(selectedDate, { enabled: () => open.value });

watch(open, (val) => {
    if (val) {
        selectedDate.value = format(new Date(), 'yyyy-MM-dd');
        dateSlideName.value = 'slide-forward';
    }
});

const visibleActions = computed(() => {
    const list = data.value ?? [];
    return showCompleted.value ? list : list.filter((action) => action.completed_at === null);
});

const formattedDate = computed(() => format(parseISO(selectedDate.value), 'EEE d MMM yyyy'));

const hasHiddenCompleted = computed(
    () => !showCompleted.value && (data.value?.length ?? 0) > 0 && visibleActions.value.length === 0,
);

const emptyTitle = computed(() => (hasHiddenCompleted.value ? 'All caught up' : 'No order actions'));

const emptySubtitle = computed(() =>
    hasHiddenCompleted.value ?
        'Toggle Show completed to view completed actions.'
    :   'No actions scheduled for this day.',
);

function prevDay() {
    dateSlideName.value = 'slide-back';
    selectedDate.value = format(subDays(parseISO(selectedDate.value), 1), 'yyyy-MM-dd');
}

function nextDay() {
    dateSlideName.value = 'slide-forward';
    selectedDate.value = format(addDays(parseISO(selectedDate.value), 1), 'yyyy-MM-dd');
}
</script>

<style scoped>
.action-list-enter-active,
.action-list-leave-active {
    transition:
        opacity 0.25s ease,
        transform 0.25s ease;
}

.action-list-enter-from,
.action-list-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

.action-list-leave-active {
    position: absolute;
    width: 100%;
}

.action-list-move {
    transition: transform 0.25s ease;
}

.slide-forward-enter-active,
.slide-forward-leave-active,
.slide-back-enter-active,
.slide-back-leave-active {
    transition:
        opacity 0.18s ease,
        transform 0.18s ease;
}

.slide-forward-enter-from {
    opacity: 0;
    transform: translateX(16px);
}
.slide-forward-leave-to {
    opacity: 0;
    transform: translateX(-16px);
}

.slide-back-enter-from {
    opacity: 0;
    transform: translateX(-16px);
}
.slide-back-leave-to {
    opacity: 0;
    transform: translateX(16px);
}
</style>
